import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Layout from "../../../components/layout";
import UserEditForm from "../../../components/users/UserEditForm";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  fetchUserData,
  fetchRoleData,
  fetchPermissionData,
  fetchBranchesData,
  getUser,
  getUsersLoading,
  clearUser,
} from "../../../features/users/usersSlice";
import { getLoggingUser } from "../../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../../features/settings/settingsSlice";
import { useCanAccess } from "../../../hooks/useCanAccess";
import { t } from "../../../locales";
import { route } from "../../../static/route";

const UsersEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const user = useAppSelector(getUser);
  const loading = useAppSelector(getUsersLoading);
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch) {
      if (!canAccess("users.update")) {
        navigate(route.dashboard);
        return;
      }
      if (id) {
        dispatch(fetchUserData({ userId: Number(id) }));
        dispatch(fetchRoleData());
        dispatch(fetchPermissionData());
        dispatch(fetchBranchesData());
      }
    }

    return () => {
      dispatch(clearUser());
    };
  }, [loggingUser, selectedBranch, id]);

  return (
    <Layout>
      <div className="px-2 py-4 md:px-6 md:py-6">
        {/* Breadcrumb */}
        <nav className="text-sm font-semibold text-gray-500 mb-4">
          <Link
            to={route.dashboard}
            className="text-brand-dark hover:underline"
          >
            {t.users.breadcrumbHome}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          <Link to={route.users} className="text-brand-dark hover:underline">
            {t.users.breadcrumbUsers}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          <span className="text-gray-500">{t.users.breadcrumbEdit}</span>
        </nav>

        {/* Page title */}
        <h1 className="text-2xl font-bold text-brand-dark mb-6">
          {t.users.breadcrumbEdit}
        </h1>

        {loading && !user ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#113F67]" />
          </div>
        ) : user ? (
          <UserEditForm />
        ) : (
          <p className="text-center text-gray-500 py-12">
            {t.users.noRecords}
          </p>
        )}
      </div>
    </Layout>
  );
};

export default UsersEditPage;
